import React, { useState, useEffect, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

const STATUSES = [
  "New Enquiry",
  "Call Not Picked",
  "Contacted",
  "Qualified",
  "Plan & Quote Sent",
  "In Pipeline",
  "Negotiating",
  "Awaiting Payment",
  "Booked",
  "Lost & Closed",
];
const STATUS_COLORS = {
  "New Enquiry": "#6366f1",
  "Call Not Picked": "#f59e0b",
  Contacted: "#3b82f6",
  Qualified: "#8b5cf6",
  "Plan & Quote Sent": "#06b6d4",
  "In Pipeline": "#10b981",
  Negotiating: "#f97316",
  "Awaiting Payment": "#ec4899",
  Booked: "#22c55e",
  "Lost & Closed": "#ef4444",
};
const CHANNELS = {
  "Instagram DM": { color: "#e1306c", bg: "#fce4ec", icon: "📸" },
  "Facebook Lead": { color: "#1877f2", bg: "#e3f2fd", icon: "📘" },
  WhatsApp: { color: "#25d366", bg: "#e8f5e9", icon: "💬" },
};
const TAG_COLORS = { Hot: "#ef4444", Warm: "#f97316", Cold: "#64748b" };
const TEAM = ["Unassigned", "Parth", "Altaf", "Riya", "Dev", "Sneha"];

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Create Lead Modal ────────────────────────────────────────────────────────
function CreateLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    channel: "Instagram DM",
    enquiry: "",
    tag: "Warm",
    assignee: "Unassigned",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setErr("Name is required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error " + res.status);
      const lead = await res.json();
      onCreated(lead);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1.5px solid #e0e0e0",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 10,
    fontFamily: "inherit",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 28,
          width: 460,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18 }}>
          ➕ Add New Lead
        </div>
        <input
          style={inputStyle}
          placeholder="Full Name *"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
        />
        <input
          style={inputStyle}
          placeholder="Phone Number"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
        />
        <input
          style={inputStyle}
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
        <select
          value={form.channel}
          onChange={(e) => set("channel", e.target.value)}
          style={inputStyle}
        >
          {Object.keys(CHANNELS).map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <textarea
          placeholder="Enquiry / Message"
          value={form.enquiry}
          rows={3}
          onChange={(e) => set("enquiry", e.target.value)}
          style={{ ...inputStyle, resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <select
            value={form.tag}
            onChange={(e) => set("tag", e.target.value)}
            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
          >
            {["Hot", "Warm", "Cold"].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <select
            value={form.assignee}
            onChange={(e) => set("assignee", e.target.value)}
            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
          >
            {TEAM.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
        {err && (
          <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>
            ⚠️ {err}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: 10,
              borderRadius: 8,
              border: "1.5px solid #e0e0e0",
              background: "#fff",
              color: "#555",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1,
              padding: 10,
              borderRadius: 8,
              border: "none",
              background: saving ? "#aaa" : "#6366f1",
              color: "#fff",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Create Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Follow Up Modal ──────────────────────────────────────────────────────────
function FollowUpModal({ lead, onClose }) {
  const [type, setType] = useState("whatsapp");
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const send = () => {
    setSent(true);
    setTimeout(onClose, 1600);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 28,
          width: 420,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        }}
      >
        {sent ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 44 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12 }}>
              Follow-up Sent!
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              Send Follow-Up
            </div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
              To: <b>{lead.name}</b>
              {lead.phone ? ` · ${lead.phone}` : ""}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {["whatsapp", "email"].map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  style={{
                    flex: 1,
                    padding: 9,
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    border: "1.5px solid",
                    borderColor: type === t ? "#6366f1" : "#e0e0e0",
                    background: type === t ? "#eef2ff" : "#fff",
                    color: type === t ? "#6366f1" : "#666",
                  }}
                >
                  {t === "whatsapp" ? "💬 WhatsApp" : "📧 Email"}
                </button>
              ))}
            </div>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={4}
              placeholder={`Write your ${type} message…`}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1.5px solid #e0e0e0",
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  border: "1.5px solid #e0e0e0",
                  background: "#fff",
                  color: "#555",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={send}
                disabled={!msg}
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  border: "none",
                  background: msg ? "#25d366" : "#ccc",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: msg ? "pointer" : "not-allowed",
                }}
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Process Meta Lead Button ─────────────────────────────────────────────────
function ProcessMetaLeadButton({ lead, onProcessed }) {
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      // Strip "meta_" prefix to get the raw Meta lead ID
      const meta_lead_id = lead.id.startsWith("meta_")
        ? lead.id.replace("meta_", "")
        : lead.id;

      const res = await fetch(`${API}/leads/process-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meta_lead_id,
          name: lead.name,
          phone: lead.phone || "",
          email: lead.email || "",
          channel: lead.channel || "Facebook Lead",
          enquiry: lead.enquiry || "",
          form_id: lead.form_id || "",
          page_id: lead.page_id || "",
          field_data: lead.field_data || [],
          raw_payload: lead,
        }),
      });

      if (!res.ok) throw new Error("Failed " + res.status);
      const { lead: savedLead, already_existed } = await res.json();

      setDone(true);
      onProcessed(savedLead, already_existed);
    } catch (e) {
      alert("Error processing lead: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (done) {
    return (
      <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 700 }}>
        ✅ Saved to DB
      </span>
    );
  }

  return (
    <button
      onClick={handleProcess}
      disabled={processing}
      style={{
        background: processing ? "#aaa" : "#6366f1",
        color: "#fff",
        border: "none",
        borderRadius: 6,
        padding: "6px 11px",
        fontSize: 11,
        fontWeight: 700,
        cursor: processing ? "not-allowed" : "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {processing ? "Saving…" : "⚡ Process Lead"}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ChannelLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track which meta lead IDs are already in DB (to mark them as processed)
  const [processedMetaIds, setProcessedMetaIds] = useState(new Set());

  const [activeStatus, setActiveStatus] = useState("Incoming Leads");
  const [activeTag, setActiveTag] = useState("All");
  const [activeChannel, setActiveChannel] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [expandedLead, setExpandedLead] = useState(null);
  const [followUpLead, setFollowUpLead] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (activeStatus !== "All" && activeStatus !== "Incoming Leads")
        p.set("status", activeStatus);
      if (activeChannel !== "All") p.set("channel", activeChannel);
      if (activeTag !== "All") p.set("tag", activeTag);
      if (search) p.set("search", search);
      p.set("limit", "100");

      const res = await fetch(`${API}/leads?${p}`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();

      const allLeads = data.leads || [];

      // Build a set of meta_lead_ids that are already in the DB
      // DB leads have numeric ids (not starting with "meta_")
      const dbMetaIds = new Set(
        allLeads
          .filter((l) => !String(l.id).startsWith("meta_") && l.meta_lead_id)
          .map((l) => l.meta_lead_id),
      );
      setProcessedMetaIds(dbMetaIds);

      setLeads(allLeads);
      setStats(data.stats || {});
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, activeChannel, activeTag, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Determine which leads to show based on active status tab
  const visibleLeads = (() => {
    if (activeStatus === "Incoming Leads") {
      // Show only unprocessed Meta leads (id starts with "meta_")
      return leads.filter((l) => {
        if (!String(l.id).startsWith("meta_")) return false;
        const rawMetaId = String(l.id).replace("meta_", "");
        return !processedMetaIds.has(rawMetaId);
      });
    }
    if (activeStatus === "All Processed Leads") {
      // Show only DB leads (no "meta_" prefix)
      return leads.filter((l) => !String(l.id).startsWith("meta_"));
    }
    // Specific status — show only DB leads with that status
    return leads.filter(
      (l) => !String(l.id).startsWith("meta_") && l.status === activeStatus,
    );
  })();

  const patchLead = async (id, field, value) => {
    setUpdatingId(id);
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );
    try {
      const res = await fetch(`${API}/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("patch failed");
    } catch {
      fetchLeads();
    } finally {
      setUpdatingId(null);
    }
  };

  const addRemark = (lead) => {
    const r = prompt("Remark:", lead.remark || "");
    if (r !== null) patchLead(lead.id, "remark", r);
  };

  const handleCreated = (lead) => {
    setLeads((prev) => [lead, ...prev]);
    setTotal((t) => t + 1);
  };

  // Called when a Meta lead is successfully processed/saved
  const handleMetaLeadProcessed = (savedLead, alreadyExisted) => {
    // Add the meta_lead_id to the processed set so it disappears from "Incoming Leads"
    setProcessedMetaIds((prev) => {
      const next = new Set(prev);
      next.add(savedLead.meta_lead_id);
      return next;
    });
    // Also inject the saved DB lead into our leads list
    setLeads((prev) => {
      const alreadyInList = prev.some((l) => l.id === savedLead.id);
      return alreadyInList ? prev : [savedLead, ...prev];
    });
  };

  const isIncomingView = activeStatus === "Incoming Leads";

  const newMetaLeadsCount = leads.filter((l) => {
    if (!String(l.id).startsWith("meta_")) return false;

    const rawMetaId = String(l.id).replace("meta_", "");

    return !processedMetaIds.has(rawMetaId);
  }).length;

  const processedLeadsCount = leads.filter(
    (l) => !String(l.id).startsWith("meta_"),
  ).length;

  const whatsappLeadsCount = leads.filter(
    (l) => l.channel === "WhatsApp",
  ).length;

  const instagramFacebookCount = leads.filter(
    (l) => l.channel === "Instagram DM" || l.channel === "Facebook Lead",
  ).length;

  return (
    <div
      style={{
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
        background: "#f8f9fb",
        minHeight: "100vh",
        color: "#1a1a2e",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e8eaed",
          padding: "16px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" }}
          >
            Zorba Channel Leads
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Instagram · Facebook · WhatsApp
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={fetchLeads}
            title="Refresh"
            style={{
              background: "#f1f5f9",
              border: "none",
              borderRadius: 8,
              padding: "9px 14px",
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            🔄
          </button>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              background: "#6366f1",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "9px 20px",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            ＋ New Lead
          </button>
        </div>
      </div>

      <div style={{ padding: "24px 28px" }}>
        {/* Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 16,
            marginBottom: 24,
          }}
        >
          {[
            {
              label: "New Meta Leads",
              value: newMetaLeadsCount || 0,
              color: "#6366f1",
            },
            {
              label: "Processed Enquiries",
              value: processedLeadsCount || 0,
              color: "#f97316",
            },
            {
              label: "Whats-App Leads",
              value: whatsappLeadsCount || 0,
              color: "#22c55e",
            },
            {
              label: "Instagram + Facebook Leads",
              value: instagramFacebookCount || 0,
              color: "#e1306c",
            },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "20px 24px",
                border: "1px solid #e8eaed",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Channel pills */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {["All", "Instagram DM", "Facebook Lead", "WhatsApp"].map((ch) => (
            <button
              key={ch}
              onClick={() => setActiveChannel(ch)}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: "1.5px solid",
                borderColor: activeChannel === ch ? "#6366f1" : "#e0e0e0",
                background: activeChannel === ch ? "#eef2ff" : "#fff",
                color: activeChannel === ch ? "#6366f1" : "#555",
              }}
            >
              {ch !== "All" && CHANNELS[ch]?.icon + " "}
              {ch}
            </button>
          ))}
        </div>

        {/* Status pipeline */}
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            paddingBottom: 6,
            marginBottom: 14,
          }}
        >
          {["Incoming Leads", "All Processed Leads", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setActiveStatus(s)}
              style={{
                whiteSpace: "nowrap",
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                border: "1.5px solid",
                borderColor:
                  activeStatus === s
                    ? STATUS_COLORS[s] || "#6366f1"
                    : "#e0e0e0",
                background:
                  activeStatus === s
                    ? (STATUS_COLORS[s] || "#6366f1") + "18"
                    : "#fff",
                color:
                  activeStatus === s ? STATUS_COLORS[s] || "#6366f1" : "#666",
              }}
            >
              {s === "Incoming Leads" ? "📥 " : ""}
              {s}
            </button>
          ))}
        </div>

        {/* Search + tag */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 18,
            alignItems: "center",
          }}
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="🔍  Search by name, phone, enquiry…"
            style={{
              flex: 1,
              padding: "9px 14px",
              borderRadius: 8,
              border: "1.5px solid #e0e0e0",
              fontSize: 13,
              outline: "none",
              background: "#fff",
              fontFamily: "inherit",
            }}
          />
          {["All", "Hot", "Warm", "Cold"].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTag(t)}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: "1.5px solid",
                borderColor:
                  activeTag === t ? TAG_COLORS[t] || "#6366f1" : "#e0e0e0",
                background:
                  activeTag === t
                    ? (TAG_COLORS[t] || "#6366f1") + "18"
                    : "#fff",
                color: activeTag === t ? TAG_COLORS[t] || "#6366f1" : "#666",
              }}
            >
              {t}
            </button>
          ))}
          <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
            {loading ? "Loading…" : `${visibleLeads.length} leads`}
          </span>
        </div>

        {/* Incoming Leads banner */}
        {isIncomingView && !loading && (
          <div
            style={{
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              borderRadius: 10,
              padding: "10px 16px",
              marginBottom: 14,
              fontSize: 13,
              color: "#4338ca",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            📥 <b>{visibleLeads.length}</b> unprocessed Meta leads — click{" "}
            <b>⚡ Process Lead</b> to save them to the database and start
            managing them.
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
              color: "#ef4444",
              fontSize: 13,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Table */}
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e8eaed",
            overflow: "hidden",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: 56, color: "#aaa" }}>
              Loading leads…
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f8f9fb",
                    borderBottom: "1px solid #e8eaed",
                  }}
                >
                  {[
                    "#",
                    "Lead Details",
                    "Enquiry",
                    isIncomingView ? "Meta Fields" : "Status",
                    isIncomingView ? "Action" : "Assigned To",
                    ...(isIncomingView ? [] : ["Remark", "Actions"]),
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#888",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleLeads.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isIncomingView ? 5 : 7}
                      style={{
                        textAlign: "center",
                        padding: 56,
                        color: "#bbb",
                      }}
                    >
                      {isIncomingView
                        ? "No new unprocessed Meta leads. They'll appear here automatically via Meta webhooks."
                        : "No leads found for this filter."}
                    </td>
                  </tr>
                ) : (
                  visibleLeads.map((lead, idx) => {
                    const ch = CHANNELS[lead.channel] || {
                      color: "#888",
                      bg: "#f1f5f9",
                      icon: "📩",
                    };
                    const isExpanded = expandedLead === lead.id;
                    const dimmed = updatingId === lead.id;
                    const isMetaLead = String(lead.id).startsWith("meta_");

                    return (
                      <React.Fragment key={lead.id}>
                        <tr
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                            background: idx % 2 === 0 ? "#fff" : "#fafafa",
                            opacity: dimmed ? 0.55 : 1,
                            transition: "opacity 0.2s",
                          }}
                        >
                          {/* # */}
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#bbb",
                              fontWeight: 600,
                            }}
                          >
                            {idx + 1}
                          </td>

                          {/* Lead Details */}
                          <td style={{ padding: "12px 16px", minWidth: 180 }}>
                            <div style={{ fontWeight: 700, marginBottom: 5 }}>
                              {lead.name}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                marginBottom: 4,
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  background: ch.bg,
                                  color: ch.color,
                                  padding: "2px 9px",
                                  borderRadius: 20,
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}
                              >
                                {ch.icon} {lead.channel}
                              </span>
                              {lead.tag && (
                                <span
                                  style={{
                                    background: TAG_COLORS[lead.tag] + "18",
                                    color: TAG_COLORS[lead.tag],
                                    padding: "2px 7px",
                                    borderRadius: 20,
                                    fontSize: 10,
                                    fontWeight: 700,
                                  }}
                                >
                                  {lead.tag}
                                </span>
                              )}
                            </div>
                            {lead.phone && (
                              <div style={{ color: "#555", fontSize: 12 }}>
                                📞 {lead.phone}
                              </div>
                            )}
                            {lead.email && (
                              <div style={{ color: "#888", fontSize: 11 }}>
                                ✉️ {lead.email}
                              </div>
                            )}
                            <div
                              style={{
                                color: "#bbb",
                                fontSize: 11,
                                marginTop: 3,
                              }}
                            >
                              🕐 {timeAgo(lead.created_at)}
                            </div>
                          </td>

                          {/* Enquiry */}
                          <td style={{ padding: "12px 16px", maxWidth: 220 }}>
                            {/* <div
                              style={{
                                color: "#444",
                                fontSize: 12,
                                lineHeight: 1.5,
                              }}
                            >
                              {lead.enquiry || "—"}
                            </div> */}
                            {/* New Style */}
                            <div
                              style={{
                                color: "#444",
                                fontSize: 12,
                                lineHeight: 1.7,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {lead.enquiry
                                ? lead.enquiry
                                    .split("\n")
                                    .map((line, index) => {
                                      const cleanLine = line
                                        .replace(/^•\s*/, "")
                                        .trim();

                                      // Split by first :
                                      const parts = cleanLine.split(":");
                                      const question = parts[0];
                                      const answer = parts
                                        .slice(1)
                                        .join(":")
                                        .trim();

                                      return (
                                        <div
                                          key={index}
                                          style={{ marginBottom: 8 }}
                                        >
                                          <div>➤ {question}:</div>

                                          {answer && (
                                            <div
                                              style={{
                                                marginLeft: 14,
                                                fontWeight: 700,
                                                color: "#111",
                                              }}
                                            >
                                              ↳ {answer}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                : "—"}
                            </div>
                          </td>

                          {isIncomingView ? (
                            <>
                              {/* Meta Fields (raw field_data) */}
                              <td
                                style={{ padding: "12px 16px", maxWidth: 220 }}
                              >
                                {(lead.field_data || []).length > 0 ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 3,
                                    }}
                                  >
                                    {lead.field_data.map((f, fi) => (
                                      <div
                                        key={fi}
                                        style={{ fontSize: 11, color: "#666" }}
                                      >
                                        <span
                                          style={{
                                            color: "#aaa",
                                            textTransform: "uppercase",
                                            fontSize: 10,
                                          }}
                                        >
                                          {f.name.replace(/_/g, " ")}:
                                        </span>{" "}
                                        <span
                                          style={{
                                            fontWeight: 600,
                                            color: "#333",
                                          }}
                                        >
                                          {f.values?.[0] || "—"}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span style={{ color: "#bbb", fontSize: 12 }}>
                                    —
                                  </span>
                                )}
                              </td>

                              {/* Process Action */}
                              <td style={{ padding: "12px 16px" }}>
                                <ProcessMetaLeadButton
                                  lead={lead}
                                  onProcessed={handleMetaLeadProcessed}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              {/* Status */}
                              <td style={{ padding: "12px 16px" }}>
                                <select
                                  value={lead.status}
                                  onChange={(e) =>
                                    patchLead(lead.id, "status", e.target.value)
                                  }
                                  style={{
                                    padding: "5px 8px",
                                    borderRadius: 7,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    outline: "none",
                                    border:
                                      "1.5px solid " +
                                      (STATUS_COLORS[lead.status] || "#ccc"),
                                    background:
                                      (STATUS_COLORS[lead.status] || "#ccc") +
                                      "18",
                                    color: STATUS_COLORS[lead.status] || "#555",
                                  }}
                                >
                                  {STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Assigned To */}
                              <td style={{ padding: "12px 16px" }}>
                                <select
                                  value={lead.assignee || "Unassigned"}
                                  onChange={(e) =>
                                    patchLead(
                                      lead.id,
                                      "assignee",
                                      e.target.value,
                                    )
                                  }
                                  style={{
                                    padding: "5px 8px",
                                    borderRadius: 7,
                                    border: "1.5px solid #e0e0e0",
                                    background: "#fff",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    outline: "none",
                                  }}
                                >
                                  {TEAM.map((t) => (
                                    <option key={t}>{t}</option>
                                  ))}
                                </select>
                              </td>

                              {/* Remark */}
                              <td style={{ padding: "12px 16px" }}>
                                {lead.remark ? (
                                  <div
                                    onClick={() => addRemark(lead)}
                                    title="Click to edit"
                                    style={{
                                      fontSize: 12,
                                      color: "#444",
                                      background: "#f8f9fb",
                                      borderRadius: 6,
                                      padding: "4px 9px",
                                      maxWidth: 160,
                                      cursor: "pointer",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {lead.remark}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => addRemark(lead)}
                                    style={{
                                      fontSize: 11,
                                      color: "#6366f1",
                                      background: "none",
                                      border: "1px dashed #6366f1",
                                      borderRadius: 6,
                                      padding: "3px 9px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    + Add Remark
                                  </button>
                                )}
                              </td>

                              {/* Actions */}
                              <td style={{ padding: "12px 16px" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 5,
                                  }}
                                >
                                  <button
                                    onClick={() => setFollowUpLead(lead)}
                                    style={{
                                      background: "#25d366",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 6,
                                      padding: "5px 10px",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      cursor: "pointer",
                                    }}
                                  >
                                    💬 Follow Up
                                  </button>
                                  <button
                                    onClick={() =>
                                      setExpandedLead(
                                        isExpanded ? null : lead.id,
                                      )
                                    }
                                    style={{
                                      background: "#f1f5f9",
                                      color: "#555",
                                      border: "none",
                                      borderRadius: 6,
                                      padding: "5px 10px",
                                      fontSize: 11,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {isExpanded ? "▲ Less" : "▼ Details"}
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>

                        {/* Expanded row (only for DB leads) */}
                        {!isMetaLead && isExpanded && (
                          <tr style={{ background: "#f8f9fb" }}>
                            <td colSpan={7} style={{ padding: "14px 24px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 28,
                                  flexWrap: "wrap",
                                }}
                              >
                                {[
                                  ["Lead ID", lead.id],
                                  ["Meta ID", lead.meta_lead_id || "—"],
                                  ["Email", lead.email || "—"],
                                  ["Phone", lead.phone || "—"],
                                  ["Channel", lead.channel],
                                  ["Assigned To", lead.assignee || "—"],
                                  ["Status", lead.status],
                                  ["Tag", lead.tag || "—"],
                                  ["Remark", lead.remark || "—"],
                                  [
                                    "Received",
                                    lead.created_at
                                      ? new Date(
                                          lead.created_at,
                                        ).toLocaleString()
                                      : "—",
                                  ],
                                ].map(([label, val]) => (
                                  <div key={label} style={{ minWidth: 100 }}>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#aaa",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        marginBottom: 3,
                                      }}
                                    >
                                      {label}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        color:
                                          label === "Status"
                                            ? STATUS_COLORS[val]
                                            : "#333",
                                        fontWeight:
                                          label === "Status" ? 700 : 400,
                                      }}
                                    >
                                      {String(val)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {followUpLead && (
        <FollowUpModal
          lead={followUpLead}
          onClose={() => setFollowUpLead(null)}
        />
      )}
      {showCreate && (
        <CreateLeadModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default ChannelLeads;
//working old code
// import React, { useState, useEffect, useCallback } from "react";

// // ── matches your app.js: app.use('/web-backend', feedBackRoutes)
// const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

// const STATUSES = [
//   "New Enquiry",
//   "Call Not Picked",
//   "Contacted",
//   "Qualified",
//   "Plan & Quote Sent",
//   "In Pipeline",
//   "Negotiating",
//   "Awaiting Payment",
//   "Booked",
//   "Lost & Closed",
// ];
// const STATUS_COLORS = {
//   "New Enquiry": "#6366f1",
//   "Call Not Picked": "#f59e0b",
//   Contacted: "#3b82f6",
//   Qualified: "#8b5cf6",
//   "Plan & Quote Sent": "#06b6d4",
//   "In Pipeline": "#10b981",
//   Negotiating: "#f97316",
//   "Awaiting Payment": "#ec4899",
//   Booked: "#22c55e",
//   "Lost & Closed": "#ef4444",
// };
// const CHANNELS = {
//   "Instagram DM": { color: "#e1306c", bg: "#fce4ec", icon: "📸" },
//   "Facebook Lead": { color: "#1877f2", bg: "#e3f2fd", icon: "📘" },
//   WhatsApp: { color: "#25d366", bg: "#e8f5e9", icon: "💬" },
// };
// const TAG_COLORS = { Hot: "#ef4444", Warm: "#f97316", Cold: "#64748b" };
// const TEAM = ["Unassigned", "Parth", "Altaf", "Riya", "Dev", "Sneha"];

// function timeAgo(dateStr) {
//   if (!dateStr) return "—";
//   const diff = (Date.now() - new Date(dateStr)) / 1000;
//   if (diff < 60) return "Just now";
//   if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
//   if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
//   return `${Math.floor(diff / 86400)}d ago`;
// }

// // ─── Create Lead Modal ────────────────────────────────────────────────────────
// function CreateLeadModal({ onClose, onCreated }) {
//   const [form, setForm] = useState({
//     name: "",
//     phone: "",
//     email: "",
//     channel: "Instagram DM",
//     enquiry: "",
//     tag: "Warm",
//     assignee: "Unassigned",
//   });
//   const [saving, setSaving] = useState(false);
//   const [err, setErr] = useState("");

//   const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

//   const handleSubmit = async () => {
//     if (!form.name.trim()) {
//       setErr("Name is required");
//       return;
//     }
//     setSaving(true);
//     setErr("");
//     try {
//       const res = await fetch(`${API}/leads`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(form),
//       });
//       if (!res.ok) throw new Error("Server error " + res.status);
//       const lead = await res.json();
//       onCreated(lead);
//       onClose();
//     } catch (e) {
//       setErr(e.message);
//     } finally {
//       setSaving(false);
//     }
//   };

//   const inputStyle = {
//     width: "100%",
//     padding: "9px 12px",
//     borderRadius: 8,
//     border: "1.5px solid #e0e0e0",
//     fontSize: 13,
//     outline: "none",
//     boxSizing: "border-box",
//     marginBottom: 10,
//     fontFamily: "inherit",
//   };

//   return (
//     <div
//       style={{
//         position: "fixed",
//         inset: 0,
//         background: "rgba(0,0,0,0.5)",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         zIndex: 200,
//       }}
//     >
//       <div
//         style={{
//           background: "#fff",
//           borderRadius: 16,
//           padding: 28,
//           width: 460,
//           boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
//           maxHeight: "90vh",
//           overflowY: "auto",
//         }}
//       >
//         <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18 }}>
//           ➕ Add New Lead
//         </div>

//         <input
//           style={inputStyle}
//           placeholder="Full Name *"
//           value={form.name}
//           onChange={(e) => set("name", e.target.value)}
//         />
//         <input
//           style={inputStyle}
//           placeholder="Phone Number"
//           value={form.phone}
//           onChange={(e) => set("phone", e.target.value)}
//         />
//         <input
//           style={inputStyle}
//           placeholder="Email"
//           type="email"
//           value={form.email}
//           onChange={(e) => set("email", e.target.value)}
//         />

//         <select
//           value={form.channel}
//           onChange={(e) => set("channel", e.target.value)}
//           style={inputStyle}
//         >
//           {Object.keys(CHANNELS).map((c) => (
//             <option key={c}>{c}</option>
//           ))}
//         </select>

//         <textarea
//           placeholder="Enquiry / Message"
//           value={form.enquiry}
//           rows={3}
//           onChange={(e) => set("enquiry", e.target.value)}
//           style={{ ...inputStyle, resize: "vertical" }}
//         />

//         <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
//           <select
//             value={form.tag}
//             onChange={(e) => set("tag", e.target.value)}
//             style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
//           >
//             {["Hot", "Warm", "Cold"].map((t) => (
//               <option key={t}>{t}</option>
//             ))}
//           </select>
//           <select
//             value={form.assignee}
//             onChange={(e) => set("assignee", e.target.value)}
//             style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
//           >
//             {TEAM.map((t) => (
//               <option key={t}>{t}</option>
//             ))}
//           </select>
//         </div>

//         {err && (
//           <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>
//             ⚠️ {err}
//           </div>
//         )}

//         <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
//           <button
//             onClick={onClose}
//             style={{
//               flex: 1,
//               padding: 10,
//               borderRadius: 8,
//               border: "1.5px solid #e0e0e0",
//               background: "#fff",
//               color: "#555",
//               fontWeight: 600,
//               cursor: "pointer",
//             }}
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleSubmit}
//             disabled={saving}
//             style={{
//               flex: 1,
//               padding: 10,
//               borderRadius: 8,
//               border: "none",
//               background: saving ? "#aaa" : "#6366f1",
//               color: "#fff",
//               fontWeight: 700,
//               cursor: saving ? "not-allowed" : "pointer",
//             }}
//           >
//             {saving ? "Saving…" : "Create Lead"}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── Follow Up Modal ──────────────────────────────────────────────────────────
// function FollowUpModal({ lead, onClose }) {
//   const [type, setType] = useState("whatsapp");
//   const [msg, setMsg] = useState("");
//   const [sent, setSent] = useState(false);

//   const send = () => {
//     // TODO: connect to your WhatsApp/email sender API
//     setSent(true);
//     setTimeout(onClose, 1600);
//   };

//   return (
//     <div
//       style={{
//         position: "fixed",
//         inset: 0,
//         background: "rgba(0,0,0,0.5)",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         zIndex: 100,
//       }}
//     >
//       <div
//         style={{
//           background: "#fff",
//           borderRadius: 16,
//           padding: 28,
//           width: 420,
//           boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
//         }}
//       >
//         {sent ? (
//           <div style={{ textAlign: "center", padding: "24px 0" }}>
//             <div style={{ fontSize: 44 }}>✅</div>
//             <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12 }}>
//               Follow-up Sent!
//             </div>
//           </div>
//         ) : (
//           <>
//             <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
//               Send Follow-Up
//             </div>
//             <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
//               To: <b>{lead.name}</b>
//               {lead.phone ? ` · ${lead.phone}` : ""}
//             </div>
//             <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
//               {["whatsapp", "email"].map((t) => (
//                 <button
//                   key={t}
//                   onClick={() => setType(t)}
//                   style={{
//                     flex: 1,
//                     padding: 9,
//                     borderRadius: 8,
//                     fontWeight: 600,
//                     fontSize: 13,
//                     cursor: "pointer",
//                     border: "1.5px solid",
//                     borderColor: type === t ? "#6366f1" : "#e0e0e0",
//                     background: type === t ? "#eef2ff" : "#fff",
//                     color: type === t ? "#6366f1" : "#666",
//                   }}
//                 >
//                   {t === "whatsapp" ? "💬 WhatsApp" : "📧 Email"}
//                 </button>
//               ))}
//             </div>
//             <textarea
//               value={msg}
//               onChange={(e) => setMsg(e.target.value)}
//               rows={4}
//               placeholder={`Write your ${type} message…`}
//               style={{
//                 width: "100%",
//                 padding: "10px 12px",
//                 borderRadius: 8,
//                 border: "1.5px solid #e0e0e0",
//                 fontSize: 13,
//                 resize: "vertical",
//                 outline: "none",
//                 boxSizing: "border-box",
//                 fontFamily: "inherit",
//               }}
//             />
//             <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
//               <button
//                 onClick={onClose}
//                 style={{
//                   flex: 1,
//                   padding: 10,
//                   borderRadius: 8,
//                   border: "1.5px solid #e0e0e0",
//                   background: "#fff",
//                   color: "#555",
//                   fontWeight: 600,
//                   cursor: "pointer",
//                 }}
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={send}
//                 disabled={!msg}
//                 style={{
//                   flex: 1,
//                   padding: 10,
//                   borderRadius: 8,
//                   border: "none",
//                   background: msg ? "#25d366" : "#ccc",
//                   color: "#fff",
//                   fontWeight: 700,
//                   cursor: msg ? "pointer" : "not-allowed",
//                 }}
//               >
//                 Send
//               </button>
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// }

// // ─── Main Component ───────────────────────────────────────────────────────────
// const ChannelLeads = () => {
//   const [leads, setLeads] = useState([]);
//   const [stats, setStats] = useState({});
//   const [total, setTotal] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [activeStatus, setActiveStatus] = useState("Incoming Leads");
//   const [activeTag, setActiveTag] = useState("All");
//   const [activeChannel, setActiveChannel] = useState("All");
//   const [searchInput, setSearchInput] = useState("");
//   const [search, setSearch] = useState("");

//   const [expandedLead, setExpandedLead] = useState(null);
//   const [followUpLead, setFollowUpLead] = useState(null);
//   const [showCreate, setShowCreate] = useState(false);
//   const [updatingId, setUpdatingId] = useState(null);

//   // Debounce search
//   useEffect(() => {
//     const t = setTimeout(() => setSearch(searchInput), 400);
//     return () => clearTimeout(t);
//   }, [searchInput]);

//   const fetchLeads = useCallback(async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const p = new URLSearchParams();
//       if (activeStatus !== "All") p.set("status", activeStatus);
//       if (activeChannel !== "All") p.set("channel", activeChannel);
//       if (activeTag !== "All") p.set("tag", activeTag);
//       if (search) p.set("search", search);
//       p.set("limit", "100");

//       const res = await fetch(`${API}/leads?${p}`);
//       if (!res.ok) throw new Error(`API error ${res.status}`);
//       const data = await res.json();
//       setLeads(data.leads || []);
//       setStats(data.stats || {});
//       setTotal(data.total || 0);
//     } catch (e) {
//       setError(e.message);
//     } finally {
//       setLoading(false);
//     }
//   }, [activeStatus, activeChannel, activeTag, search]);

//   useEffect(() => {
//     fetchLeads();
//   }, [fetchLeads]);

//   // Optimistic inline update → PATCH /web-backend/leads/:id
//   const patchLead = async (id, field, value) => {
//     setUpdatingId(id);
//     setLeads((prev) =>
//       prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
//     );
//     try {
//       const res = await fetch(`${API}/leads/${id}`, {
//         method: "PATCH",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ [field]: value }),
//       });
//       if (!res.ok) throw new Error("patch failed");
//     } catch {
//       fetchLeads(); // revert on error
//     } finally {
//       setUpdatingId(null);
//     }
//   };

//   const addRemark = (lead) => {
//     const r = prompt("Remark:", lead.remark || "");
//     if (r !== null) patchLead(lead.id, "remark", r);
//   };

//   const handleCreated = (lead) => {
//     setLeads((prev) => [lead, ...prev]);
//     setTotal((t) => t + 1);
//   };

//   // ── render ──────────────────────────────────────────────────────────────────
//   return (
//     <div
//       style={{
//         fontFamily: "'DM Sans','Segoe UI',sans-serif",
//         background: "#f8f9fb",
//         minHeight: "100vh",
//         color: "#1a1a2e",
//       }}
//     >
//       {/* Header */}
//       <div
//         style={{
//           background: "#fff",
//           borderBottom: "1px solid #e8eaed",
//           padding: "16px 28px",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//         }}
//       >
//         <div>
//           <div
//             style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" }}
//           >
//             Channel Leads
//           </div>
//           <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
//             Instagram · Facebook · WhatsApp
//           </div>
//         </div>
//         <div style={{ display: "flex", gap: 10 }}>
//           <button
//             onClick={fetchLeads}
//             title="Refresh"
//             style={{
//               background: "#f1f5f9",
//               border: "none",
//               borderRadius: 8,
//               padding: "9px 14px",
//               fontSize: 15,
//               cursor: "pointer",
//             }}
//           >
//             🔄
//           </button>
//           <button
//             onClick={() => setShowCreate(true)}
//             style={{
//               background: "#6366f1",
//               color: "#fff",
//               border: "none",
//               borderRadius: 8,
//               padding: "9px 20px",
//               fontWeight: 600,
//               fontSize: 13,
//               cursor: "pointer",
//             }}
//           >
//             ＋ New Lead
//           </button>
//         </div>
//       </div>

//       <div style={{ padding: "24px 28px" }}>
//         {/* Stats */}
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(4,1fr)",
//             gap: 16,
//             marginBottom: 24,
//           }}
//         >
//           {[
//             { label: "Total Leads", value: stats.total || 0, color: "#6366f1" },
//             {
//               label: "Today's Enquiries",
//               value: stats.today || 0,
//               color: "#f97316",
//             },
//             { label: "Booked", value: stats.booked || 0, color: "#22c55e" },
//             {
//               label: "Instagram + Facebook",
//               value:
//                 parseInt(stats.instagram || 0) + parseInt(stats.facebook || 0),
//               color: "#e1306c",
//             },
//           ].map((s) => (
//             <div
//               key={s.label}
//               style={{
//                 background: "#fff",
//                 borderRadius: 12,
//                 padding: "20px 24px",
//                 border: "1px solid #e8eaed",
//                 boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
//               }}
//             >
//               <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
//                 {s.label}
//               </div>
//               <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>
//                 {s.value}
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Channel pills */}
//         <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
//           {["All", "Instagram DM", "Facebook Lead", "WhatsApp"].map((ch) => (
//             <button
//               key={ch}
//               onClick={() => setActiveChannel(ch)}
//               style={{
//                 padding: "6px 14px",
//                 borderRadius: 20,
//                 fontSize: 12,
//                 fontWeight: 600,
//                 cursor: "pointer",
//                 border: "1.5px solid",
//                 borderColor: activeChannel === ch ? "#6366f1" : "#e0e0e0",
//                 background: activeChannel === ch ? "#eef2ff" : "#fff",
//                 color: activeChannel === ch ? "#6366f1" : "#555",
//               }}
//             >
//               {ch !== "All" && CHANNELS[ch]?.icon + " "}
//               {ch}
//             </button>
//           ))}
//         </div>

//         {/* Status pipeline */}
//         <div
//           style={{
//             display: "flex",
//             gap: 6,
//             overflowX: "auto",
//             paddingBottom: 6,
//             marginBottom: 14,
//           }}
//         >
//           {["Incoming Leads", "All Processed Leads", ...STATUSES].map((s) => (
//             <button
//               key={s}
//               onClick={() => setActiveStatus(s)}
//               style={{
//                 whiteSpace: "nowrap",
//                 padding: "5px 12px",
//                 borderRadius: 6,
//                 fontSize: 11,
//                 fontWeight: 600,
//                 cursor: "pointer",
//                 border: "1.5px solid",
//                 borderColor:
//                   activeStatus === s
//                     ? STATUS_COLORS[s] || "#6366f1"
//                     : "#e0e0e0",
//                 background:
//                   activeStatus === s
//                     ? (STATUS_COLORS[s] || "#6366f1") + "18"
//                     : "#fff",
//                 color:
//                   activeStatus === s ? STATUS_COLORS[s] || "#6366f1" : "#666",
//               }}
//             >
//               {s}
//             </button>
//           ))}
//         </div>

//         {/* Search + tag */}
//         <div
//           style={{
//             display: "flex",
//             gap: 10,
//             marginBottom: 18,
//             alignItems: "center",
//           }}
//         >
//           <input
//             value={searchInput}
//             onChange={(e) => setSearchInput(e.target.value)}
//             placeholder="🔍  Search by name, phone, enquiry…"
//             style={{
//               flex: 1,
//               padding: "9px 14px",
//               borderRadius: 8,
//               border: "1.5px solid #e0e0e0",
//               fontSize: 13,
//               outline: "none",
//               background: "#fff",
//               fontFamily: "inherit",
//             }}
//           />
//           {["All", "Hot", "Warm", "Cold"].map((t) => (
//             <button
//               key={t}
//               onClick={() => setActiveTag(t)}
//               style={{
//                 padding: "7px 14px",
//                 borderRadius: 8,
//                 fontSize: 12,
//                 fontWeight: 600,
//                 cursor: "pointer",
//                 border: "1.5px solid",
//                 borderColor:
//                   activeTag === t ? TAG_COLORS[t] || "#6366f1" : "#e0e0e0",
//                 background:
//                   activeTag === t
//                     ? (TAG_COLORS[t] || "#6366f1") + "18"
//                     : "#fff",
//                 color: activeTag === t ? TAG_COLORS[t] || "#6366f1" : "#666",
//               }}
//             >
//               {t}
//             </button>
//           ))}
//           <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
//             {loading ? "Loading…" : `${leads.length} / ${total}`}
//           </span>
//         </div>

//         {/* Error banner */}
//         {error && (
//           <div
//             style={{
//               background: "#fef2f2",
//               border: "1px solid #fecaca",
//               borderRadius: 8,
//               padding: "12px 16px",
//               marginBottom: 16,
//               color: "#ef4444",
//               fontSize: 13,
//             }}
//           >
//             ⚠️ {error} — make sure your backend is running and the DB migration
//             has been applied.
//           </div>
//         )}

//         {/* Table */}
//         <div
//           style={{
//             background: "#fff",
//             borderRadius: 14,
//             border: "1px solid #e8eaed",
//             overflow: "hidden",
//             boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
//           }}
//         >
//           {loading ? (
//             <div style={{ textAlign: "center", padding: 56, color: "#aaa" }}>
//               Loading leads…
//             </div>
//           ) : (
//             <table
//               style={{
//                 width: "100%",
//                 borderCollapse: "collapse",
//                 fontSize: 13,
//               }}
//             >
//               <thead>
//                 <tr
//                   style={{
//                     background: "#f8f9fb",
//                     borderBottom: "1px solid #e8eaed",
//                   }}
//                 >
//                   {[
//                     "#",
//                     "Lead Details",
//                     "Enquiry",
//                     "Status",
//                     "Assigned To",
//                     "Remark",
//                     "Actions",
//                   ].map((h) => (
//                     <th
//                       key={h}
//                       style={{
//                         padding: "12px 16px",
//                         textAlign: "left",
//                         fontWeight: 600,
//                         color: "#888",
//                         whiteSpace: "nowrap",
//                       }}
//                     >
//                       {h}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {leads.length === 0 ? (
//                   <tr>
//                     <td
//                       colSpan={7}
//                       style={{
//                         textAlign: "center",
//                         padding: 56,
//                         color: "#bbb",
//                       }}
//                     >
//                       No leads yet. They'll appear here automatically via Meta
//                       webhooks.
//                     </td>
//                   </tr>
//                 ) : (
//                   leads.map((lead, idx) => {
//                     const ch = CHANNELS[lead.channel] || {
//                       color: "#888",
//                       bg: "#f1f5f9",
//                       icon: "📩",
//                     };
//                     const isExpanded = expandedLead === lead.id;
//                     const dimmed = updatingId === lead.id;

//                     return (
//                       <React.Fragment key={lead.id}>
//                         <tr
//                           style={{
//                             borderBottom: "1px solid #f0f0f0",
//                             background: idx % 2 === 0 ? "#fff" : "#fafafa",
//                             opacity: dimmed ? 0.55 : 1,
//                             transition: "opacity 0.2s",
//                           }}
//                         >
//                           {/* # */}
//                           <td
//                             style={{
//                               padding: "12px 16px",
//                               color: "#bbb",
//                               fontWeight: 600,
//                             }}
//                           >
//                             {idx + 1}
//                           </td>

//                           {/* Lead Details */}
//                           <td style={{ padding: "12px 16px", minWidth: 180 }}>
//                             <div style={{ fontWeight: 700, marginBottom: 5 }}>
//                               {lead.name}
//                             </div>
//                             <div
//                               style={{
//                                 display: "flex",
//                                 alignItems: "center",
//                                 gap: 5,
//                                 marginBottom: 4,
//                                 flexWrap: "wrap",
//                               }}
//                             >
//                               <span
//                                 style={{
//                                   background: ch.bg,
//                                   color: ch.color,
//                                   padding: "2px 9px",
//                                   borderRadius: 20,
//                                   fontSize: 11,
//                                   fontWeight: 700,
//                                 }}
//                               >
//                                 {ch.icon} {lead.channel}
//                               </span>
//                               {lead.tag && (
//                                 <span
//                                   style={{
//                                     background: TAG_COLORS[lead.tag] + "18",
//                                     color: TAG_COLORS[lead.tag],
//                                     padding: "2px 7px",
//                                     borderRadius: 20,
//                                     fontSize: 10,
//                                     fontWeight: 700,
//                                   }}
//                                 >
//                                   {lead.tag}
//                                 </span>
//                               )}
//                             </div>
//                             {lead.phone && (
//                               <div style={{ color: "#555", fontSize: 12 }}>
//                                 📞 {lead.phone}
//                               </div>
//                             )}
//                             {lead.email && (
//                               <div style={{ color: "#888", fontSize: 11 }}>
//                                 ✉️ {lead.email}
//                               </div>
//                             )}
//                             <div
//                               style={{
//                                 color: "#bbb",
//                                 fontSize: 11,
//                                 marginTop: 3,
//                               }}
//                             >
//                               🕐 {timeAgo(lead.created_at)}
//                             </div>
//                           </td>

//                           {/* Enquiry */}
//                           <td style={{ padding: "12px 16px", maxWidth: 220 }}>
//                             <div
//                               style={{
//                                 color: "#444",
//                                 fontSize: 12,
//                                 lineHeight: 1.5,
//                               }}
//                             >
//                               {lead.enquiry || "—"}
//                             </div>
//                           </td>

//                           {/* Status */}
//                           <td style={{ padding: "12px 16px" }}>
//                             <select
//                               value={lead.status}
//                               onChange={(e) =>
//                                 patchLead(lead.id, "status", e.target.value)
//                               }
//                               style={{
//                                 padding: "5px 8px",
//                                 borderRadius: 7,
//                                 fontSize: 11,
//                                 fontWeight: 700,
//                                 cursor: "pointer",
//                                 outline: "none",
//                                 border:
//                                   "1.5px solid " +
//                                   (STATUS_COLORS[lead.status] || "#ccc"),
//                                 background:
//                                   (STATUS_COLORS[lead.status] || "#ccc") + "18",
//                                 color: STATUS_COLORS[lead.status] || "#555",
//                               }}
//                             >
//                               {STATUSES.map((s) => (
//                                 <option key={s} value={s}>
//                                   {s}
//                                 </option>
//                               ))}
//                             </select>
//                           </td>

//                           {/* Assigned To */}
//                           <td style={{ padding: "12px 16px" }}>
//                             <select
//                               value={lead.assignee || "Unassigned"}
//                               onChange={(e) =>
//                                 patchLead(lead.id, "assignee", e.target.value)
//                               }
//                               style={{
//                                 padding: "5px 8px",
//                                 borderRadius: 7,
//                                 border: "1.5px solid #e0e0e0",
//                                 background: "#fff",
//                                 fontSize: 12,
//                                 cursor: "pointer",
//                                 outline: "none",
//                               }}
//                             >
//                               {TEAM.map((t) => (
//                                 <option key={t}>{t}</option>
//                               ))}
//                             </select>
//                           </td>

//                           {/* Remark */}
//                           <td style={{ padding: "12px 16px" }}>
//                             {lead.remark ? (
//                               <div
//                                 onClick={() => addRemark(lead)}
//                                 title="Click to edit"
//                                 style={{
//                                   fontSize: 12,
//                                   color: "#444",
//                                   background: "#f8f9fb",
//                                   borderRadius: 6,
//                                   padding: "4px 9px",
//                                   maxWidth: 160,
//                                   cursor: "pointer",
//                                   lineHeight: 1.4,
//                                 }}
//                               >
//                                 {lead.remark}
//                               </div>
//                             ) : (
//                               <button
//                                 onClick={() => addRemark(lead)}
//                                 style={{
//                                   fontSize: 11,
//                                   color: "#6366f1",
//                                   background: "none",
//                                   border: "1px dashed #6366f1",
//                                   borderRadius: 6,
//                                   padding: "3px 9px",
//                                   cursor: "pointer",
//                                 }}
//                               >
//                                 + Add Remark
//                               </button>
//                             )}
//                           </td>

//                           {/* Actions */}
//                           <td style={{ padding: "12px 16px" }}>
//                             <div
//                               style={{
//                                 display: "flex",
//                                 flexDirection: "column",
//                                 gap: 5,
//                               }}
//                             >
//                               <button
//                                 onClick={() => setFollowUpLead(lead)}
//                                 style={{
//                                   background: "#25d366",
//                                   color: "#fff",
//                                   border: "none",
//                                   borderRadius: 6,
//                                   padding: "5px 10px",
//                                   fontSize: 11,
//                                   fontWeight: 600,
//                                   cursor: "pointer",
//                                 }}
//                               >
//                                 💬 Follow Up
//                               </button>
//                               <button
//                                 onClick={() =>
//                                   setExpandedLead(isExpanded ? null : lead.id)
//                                 }
//                                 style={{
//                                   background: "#f1f5f9",
//                                   color: "#555",
//                                   border: "none",
//                                   borderRadius: 6,
//                                   padding: "5px 10px",
//                                   fontSize: 11,
//                                   cursor: "pointer",
//                                 }}
//                               >
//                                 {isExpanded ? "▲ Less" : "▼ Details"}
//                               </button>
//                             </div>
//                           </td>
//                         </tr>

//                         {/* Expanded row */}
//                         {isExpanded && (
//                           <tr style={{ background: "#f8f9fb" }}>
//                             <td colSpan={7} style={{ padding: "14px 24px" }}>
//                               <div
//                                 style={{
//                                   display: "flex",
//                                   gap: 28,
//                                   flexWrap: "wrap",
//                                 }}
//                               >
//                                 {[
//                                   ["Lead ID", lead.id],
//                                   ["Meta ID", lead.meta_lead_id || "—"],
//                                   ["Email", lead.email || "—"],
//                                   ["Phone", lead.phone || "—"],
//                                   ["Channel", lead.channel],
//                                   ["Assigned To", lead.assignee || "—"],
//                                   ["Status", lead.status],
//                                   ["Tag", lead.tag || "—"],
//                                   ["Remark", lead.remark || "—"],
//                                   [
//                                     "Received",
//                                     lead.created_at
//                                       ? new Date(
//                                           lead.created_at,
//                                         ).toLocaleString()
//                                       : "—",
//                                   ],
//                                 ].map(([label, val]) => (
//                                   <div key={label} style={{ minWidth: 100 }}>
//                                     <div
//                                       style={{
//                                         fontSize: 10,
//                                         color: "#aaa",
//                                         fontWeight: 700,
//                                         textTransform: "uppercase",
//                                         marginBottom: 3,
//                                       }}
//                                     >
//                                       {label}
//                                     </div>
//                                     <div
//                                       style={{
//                                         fontSize: 13,
//                                         color:
//                                           label === "Status"
//                                             ? STATUS_COLORS[val]
//                                             : "#333",
//                                         fontWeight:
//                                           label === "Status" ? 700 : 400,
//                                       }}
//                                     >
//                                       {String(val)}
//                                     </div>
//                                   </div>
//                                 ))}
//                               </div>
//                             </td>
//                           </tr>
//                         )}
//                       </React.Fragment>
//                     );
//                   })
//                 )}
//               </tbody>
//             </table>
//           )}
//         </div>
//       </div>

//       {/* Modals */}
//       {followUpLead && (
//         <FollowUpModal
//           lead={followUpLead}
//           onClose={() => setFollowUpLead(null)}
//         />
//       )}
//       {showCreate && (
//         <CreateLeadModal
//           onClose={() => setShowCreate(false)}
//           onCreated={handleCreated}
//         />
//       )}
//     </div>
//   );
// };

// export default ChannelLeads;
